import { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@material-ui/core';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PauseIcon from '@material-ui/icons/Pause';
import CloseIcon from '@material-ui/icons/Close';
import SpeedIcon from '@material-ui/icons/Speed';

const SPEEDS = [1, 2, 5, 10, 20];

function RouteReplay({ mapRef, routeId, token, onClose, lang = 'pt' }) {
  const pt = lang === 'pt';
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [speed, setSpeed] = useState(5);
  const animRef = useRef(null);
  const markerRef = useRef(null);
  const lastTimeRef = useRef(0);

  // Fetch trail points
  useEffect(() => {
    if (!routeId || !token) return;
    setLoading(true);
    fetch(`/planner-api/mobile/routes/${routeId}/trail`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        // Convert GeoJSON LineString to points array
        const coords = data?.coordinates || data?.geometry?.coordinates || [];
        const pts = coords.map((c, i) => ({
          lng: c[0], lat: c[1],
          timestamp: data.properties?.timestamps?.[i] || Date.now() - (coords.length - i) * 10000,
          speed: data.properties?.speeds?.[i] || 0,
        }));
        setPoints(pts);
        setCurrentIdx(0);
      })
      .catch(() => setPoints([]))
      .finally(() => setLoading(false));
  }, [routeId, token]);

  // Setup map layers
  useEffect(() => {
    const map = mapRef?.current;
    if (!map || points.length === 0) return;

    // Add replay trail source/layer
    if (!map.getSource('replay-trail')) {
      map.addSource('replay-trail', {
        type: 'geojson',
        data: { type: 'Feature', geometry: { type: 'LineString', coordinates: [] } },
      });
      map.addLayer({
        id: 'replay-trail-line',
        type: 'line',
        source: 'replay-trail',
        paint: { 'line-color': '#f59e0b', 'line-width': 4, 'line-opacity': 0.8 },
      });
    }

    // Create vehicle marker
    if (!markerRef.current) {
      const el = document.createElement('div');
      el.style.cssText = 'width:18px;height:18px;cursor:pointer;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.4))';
      el.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="#f59e0b"><circle cx="12" cy="12" r="10" fill="#f59e0b" stroke="#fff" stroke-width="2"/><path d="M12 6l4 8H8l4-8z" fill="#fff"/></svg>';
      const mapboxgl = window.mapboxgl || (map.constructor && map.constructor.__proto__);
      if (window.mapboxgl) {
        markerRef.current = new window.mapboxgl.Marker({ element: el, anchor: 'center' })
          .setLngLat([points[0].lng, points[0].lat])
          .addTo(map);
      }
    }

    // Fit to trail bounds
    if (points.length > 1) {
      const lngs = points.map(p => p.lng);
      const lats = points.map(p => p.lat);
      map.fitBounds(
        [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
        { padding: 80, duration: 800 }
      );
    }

    return () => {
      if (markerRef.current) { markerRef.current.remove(); markerRef.current = null; }
      if (map.getLayer('replay-trail-line')) map.removeLayer('replay-trail-line');
      if (map.getSource('replay-trail')) map.removeSource('replay-trail');
    };
  }, [points, mapRef]);

  // Animation loop
  const animate = useCallback((timestamp) => {
    if (!playing) return;
    const elapsed = timestamp - lastTimeRef.current;
    if (elapsed > 50) { // ~20fps
      lastTimeRef.current = timestamp;
      setCurrentIdx(prev => {
        const next = Math.min(prev + speed, points.length - 1);
        // Update marker position
        const map = mapRef?.current;
        if (map && points[next]) {
          if (markerRef.current) markerRef.current.setLngLat([points[next].lng, points[next].lat]);
          // Update trail line
          const trailCoords = points.slice(0, next + 1).map(p => [p.lng, p.lat]);
          const src = map.getSource('replay-trail');
          if (src) src.setData({ type: 'Feature', geometry: { type: 'LineString', coordinates: trailCoords } });
        }
        if (next >= points.length - 1) { setPlaying(false); return points.length - 1; }
        return next;
      });
    }
    animRef.current = requestAnimationFrame(animate);
  }, [playing, speed, points, mapRef]);

  useEffect(() => {
    if (playing) {
      lastTimeRef.current = performance.now();
      animRef.current = requestAnimationFrame(animate);
    }
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [playing, animate]);

  // Slider change
  function handleSliderChange(e) {
    const idx = parseInt(e.target.value);
    setCurrentIdx(idx);
    const map = mapRef?.current;
    if (map && points[idx]) {
      if (markerRef.current) markerRef.current.setLngLat([points[idx].lng, points[idx].lat]);
      const trailCoords = points.slice(0, idx + 1).map(p => [p.lng, p.lat]);
      const src = map.getSource('replay-trail');
      if (src) src.setData({ type: 'Feature', geometry: { type: 'LineString', coordinates: trailCoords } });
    }
  }

  function handleClose() {
    setPlaying(false);
    if (animRef.current) cancelAnimationFrame(animRef.current);
    onClose();
  }

  if (loading) return (
    <Box style={{
      position: 'absolute', bottom: 70, left: '50%', transform: 'translateX(-50%)', zIndex: 300,
      background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', borderRadius: 12,
      padding: '12px 20px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
    }}>
      <Typography style={{ fontSize: '0.78rem', color: '#64748b' }}>
        {pt ? 'A carregar percurso...' : 'Loading trail...'}
      </Typography>
    </Box>
  );

  if (points.length === 0) return (
    <Box style={{
      position: 'absolute', bottom: 70, left: '50%', transform: 'translateX(-50%)', zIndex: 300,
      background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', borderRadius: 12,
      padding: '12px 20px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <Typography style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
        {pt ? 'Sem dados de percurso' : 'No trail data'}
      </Typography>
      <IconButton size="small" onClick={handleClose}><CloseIcon style={{ fontSize: 16 }} /></IconButton>
    </Box>
  );

  const progress = points.length > 1 ? (currentIdx / (points.length - 1)) * 100 : 0;
  const currentPoint = points[currentIdx];
  const timeStr = currentPoint?.timestamp ? new Date(currentPoint.timestamp).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';

  return (
    <Box style={{
      position: 'absolute', bottom: 70, left: '50%', transform: 'translateX(-50%)', zIndex: 300,
      background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', borderRadius: 12,
      padding: '8px 16px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
      width: 480, maxWidth: '90vw', display: 'flex', alignItems: 'center', gap: 8,
    }}>
      {/* Play/Pause */}
      <IconButton size="small" onClick={() => { if (currentIdx >= points.length - 1) setCurrentIdx(0); setPlaying(!playing); }}
        style={{ background: playing ? '#fee2e2' : '#dcfce7', width: 32, height: 32 }}>
        {playing ? <PauseIcon style={{ fontSize: 16, color: '#ef4444' }} /> : <PlayArrowIcon style={{ fontSize: 16, color: '#22c55e' }} />}
      </IconButton>

      {/* Slider */}
      <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <input type="range" min={0} max={points.length - 1} value={currentIdx}
          onChange={handleSliderChange}
          style={{ width: '100%', height: 4, accentColor: '#f59e0b', cursor: 'pointer' }} />
        <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography style={{ fontSize: '0.58rem', color: '#94a3b8' }}>{timeStr}</Typography>
          <Typography style={{ fontSize: '0.58rem', color: '#94a3b8' }}>{Math.round(progress)}%</Typography>
        </Box>
      </Box>

      {/* Speed */}
      <Tooltip title={`${speed}x`}>
        <IconButton size="small" onClick={() => setSpeed(s => SPEEDS[(SPEEDS.indexOf(s) + 1) % SPEEDS.length])}
          style={{ width: 32, height: 32, background: '#f1f5f9' }}>
          <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b' }}>{speed}x</Typography>
        </IconButton>
      </Tooltip>

      {/* Close */}
      <IconButton size="small" onClick={handleClose} style={{ width: 32, height: 32 }}>
        <CloseIcon style={{ fontSize: 14, color: '#94a3b8' }} />
      </IconButton>
    </Box>
  );
}

export default RouteReplay;
